
import CheckCircleOutlined from '@material-ui/icons/CheckCircleOutlineOutlined'
import { authSelectors } from 'ducks/auth'
import { autoDesignSelectors, setAutoDesignStatusForProject, setInProgressDialogOpen } from 'ducks/autoDesign'
import { Button, CircularProgress, Dialog, DialogTitle } from 'opensolar-ui'
import React, { useEffect } from 'react'
import { useTranslate } from 'react-admin'
import { useDispatch, useSelector } from 'react-redux'
import { useLocation } from 'react-router-dom'
import { projectInfoSelector } from 'reducer/project/projectInfoReducer'
import { makeOpenSolarStyles } from 'themes/makeOpenSolarStyles'
import { pollAutoDesignProgress } from 'util/autoDesign'
type StyleProps = {}

const useStyles = makeOpenSolarStyles((theme) => ({
  dialogPaper: (props: StyleProps) => ({
    display: 'flex',
    flexDirection: 'column',
    gap: '32px',
    padding: '40px',
  }),
  dialogTitleContainer: (props: StyleProps) => ({
    alignItems: 'left',
    display: 'flex',
    justifyContent: 'left',
    padding: '0px',
  }),
  dialogTitle: (props: StyleProps) => ({
    color: '#212121',
    fontSize: '24px',
    fontWeight: 500,
    lineHeight: 'normal',
  }),
  divider: {
    background: '#D9DBE9',
    height: '1px',
    minHeight: '1px',
    width: '100%',
  },
  notes: {
    marginTop: 20,
    fontSize: '14px',
  },
  iconContainer: {
    height: 40,
  },
  iconWrapper: {
    display: 'flex',
    justifyContent: 'center',
  },
  loadingIcon: {
    fontSize: 30,
  },
  successIcon: {
    color: '#4CAF50',
    fontSize: 30,
  },
  buttonRow: {
    display: 'flex',
    flexDirection: 'row',
    gap: '16px',
    alignItems: 'flex-start',
    width: '100%',
  },
  buttonColumn: {
    width: '50%',
    textAlign: 'center',
  },
}))

interface AutoDesignInProgressDialogProps {
  projectId?: number
}

export const AutoDesignInProgressDialog: React.FC<AutoDesignInProgressDialogProps> = () => {
  /*
   * Open/close logic is tricky. If auto-design is running when project zone is first loaded (on any page)
   * then instantiate the dialog in the "open" state. After being added, the dialog can only be closed manually.
   */
  const location = useLocation()
  const isDesignPage = location.pathname?.includes('/design')

  // isAvailable indicates that the dialog could be opened, and can start polling for progress if necessary
  // but it will only actually open on the design page.
  const isAvailable = useSelector(autoDesignSelectors.inProgressDialogOpen)
  const isOpen = isAvailable && isDesignPage
  const projectId = useSelector(projectInfoSelector.getProjectId)
  const orgId = useSelector(authSelectors.getOrgId) as number
  const autoDesignStatusAllProjects = useSelector(autoDesignSelectors.autoDesignStatus)
  const autoDesignStatus = projectId ? autoDesignStatusAllProjects[projectId] : null
  const [status, message, progressPercentage] = autoDesignStatus || ['NOT_STARTED', '', 0]
  const isRunning = progressPercentage !== 100

  const dispatch = useDispatch()
  const translate = useTranslate()

  const handleClose = () => {
    dispatch(setInProgressDialogOpen(false))
  }

  const setAutoDesignStatus = (autoDesignStatus: [string, string, number]) => {
    if (projectId) {
      dispatch(setAutoDesignStatusForProject(projectId, autoDesignStatus))
    }
  }

  useEffect(() => {
    // When dialog opens, start polling for auto-design progress
    if (isAvailable) {
      pollAutoDesignProgress(orgId, projectId, setAutoDesignStatus)
    }
  }, [isAvailable])

  const classes = useStyles()

  return (
    <Dialog
      classes={{
        paper: classes.dialogPaper,
      }}
      maxWidth={'sm'}
      open={isOpen && isDesignPage}
      onBackdropClick={undefined}
    >
      <DialogTitle classes={{ root: classes.dialogTitleContainer }} disableTypography>
        <div className={classes.iconContainer}>
          {isRunning ? (
            <div className={classes.iconWrapper}>
              <CircularProgress size={30} thickness={4} />
            </div>
          ) : (
            <div className={classes.iconWrapper}>
              <CheckCircleOutlined className={classes.successIcon} />
            </div>
          )}
        </div>
        <div className={classes.dialogTitle}>
          {isRunning
            ? translate(`Auto-Design In Progress... ${progressPercentage}%`)
            : translate('Auto-Design Complete')}
        </div>
      </DialogTitle>
      <div className={classes.divider} />

      <div className={classes.buttonRow}>
        <div className={classes.buttonColumn}>
          <Button
            onClick={() => {
              handleClose()
            }}
            variant={'outlined'}
            color="primary"
          >
            Ignore and close
          </Button>
          <div className={classes.notes}>
            Note that if you save your design it may over-write auto-designed systems.
          </div>
        </div>

        <div className={classes.buttonColumn}>
          <Button
            onClick={() => {
              window.location.reload()
            }}
            disabled={isRunning}
            variant={isRunning ? 'outlined' : 'contained'}
            color="primary"
          >
            Load Auto-Design results
          </Button>
          <div className={classes.notes}>(Note this will clear unsaved design changes)</div>
        </div>
      </div>
    </Dialog>
  )
}

export default AutoDesignInProgressDialog
